import * as React from "react";
import { cn, formatDayOrdinal } from "lib/utils";
import { Button } from "components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "components/ui/command";
import { Check, ChevronsUpDown, Download, Search } from "lucide-react";
import { Subordinate, Division, PaymentTypeValue, PaymentType, CustomerStatusTypeValue } from "../api";

const MONTH_LABELS: Record<number, string> = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
};

export interface MonthOption {
    label: string;
    value: number;
}

interface AraDropdownsProps {
    years: string[];
    year: string;
    month: number | null;
    day: number | null;
    months: number[];
    days: number[];
    divisions: Division[];
    selectedDivision: string;
    subordinates: Subordinate[];
    selectedEcode: string;
    search: string;
    paymentType: PaymentTypeValue;
    customerStatus: CustomerStatusTypeValue;
    consignors: { cust_code: string; cust_name: string }[];
    selectedConsignor: string;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
    onCustomerStatusChange: (val: CustomerStatusTypeValue) => void;
    onPaymentTypeChange: (val: PaymentTypeValue) => void;
    onSearchChange: (value: string) => void;
    onYearChange: (year: string) => void;
    onMonthChange: (month: number) => void;
    onDayChange: (day: number) => void;
    onDivisionChange: (divisionOuCode: string) => void;
    onEcodeChange: (ecode: string) => void;
    onConsignorChange: (code: string) => void;
    downloadingBulkExcel: boolean;
    onDownloadBulkExcel: () => void;
    downloadingExcel: boolean;
    onDownloadExcel: () => void;
    showDownloadExcelButton?: boolean;
    downloadingRDDExcel: boolean;
    onDownloadRDDExcel: () => void;
    showDownloadRDDButton?: boolean;
}

const AraDropdowns: React.FC<AraDropdownsProps> = ({
    years,
    year,
    month,
    day,
    months,
    days,
    divisions,
    selectedDivision,
    subordinates,
    selectedEcode,
    search,
    paymentType,
    customerStatus,
    consignors = [],
    selectedConsignor,
    conversionType,
    onConversionChange,
    onCustomerStatusChange,
    onPaymentTypeChange,
    onSearchChange,
    onYearChange,
    onMonthChange,
    onDayChange,
    onDivisionChange,
    onEcodeChange,
    onConsignorChange,
    downloadingBulkExcel,
    onDownloadBulkExcel,
    downloadingExcel,
    onDownloadExcel,
    showDownloadExcelButton,
    downloadingRDDExcel,
    onDownloadRDDExcel,
    showDownloadRDDButton,
}) => {
    const [yearOpen, setYearOpen] = React.useState(false);
    const [monthOpen, setMonthOpen] = React.useState(false);
    const [dayOpen, setDayOpen] = React.useState(false);
    const [divisionOpen, setDivisionOpen] = React.useState(false);
    const [ecodeOpen, setEcodeOpen] = React.useState(false);
    const [consignorOpen, setConsignorOpen] = React.useState(false);

    const selectedSubordinate = subordinates.find(s => s.ecode === selectedEcode);
    const selectedDivisionObj = divisions.find(d => d.ou_code === selectedDivision);

    const safeDays = React.useMemo(
        () => [...days].sort((a, b) => b - a),
        [days]
    );

    const PAYMENT_TYPE_OPTIONS: {
        label: string;
        value: PaymentType;
    }[] = [
            { label: 'To Pay', value: 'to_pay' },
            { label: 'To be Billed', value: 'to_be_billed' },
            { label: 'Non CN', value: "non_cn" },
        ];

    return (
        <div className="flex flex-col gap-4 w-full">
            <div
                className="grid gap-4 items-start w-full"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
            >
                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Year</label>
                    <Popover open={yearOpen} onOpenChange={setYearOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between h-10 px-2.5 text-sm"
                            >
                                {year || "Select Year"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandInput placeholder="Search year..." />
                                <CommandEmpty>No year found.</CommandEmpty>
                                <CommandGroup>
                                    {years.map((y) => (
                                        <CommandItem
                                            key={y}
                                            value={y}
                                            onSelect={() => {
                                                onYearChange(y);
                                                setYearOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    year === y ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            {y}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Month</label>
                    <Popover
                        open={monthOpen}
                        onOpenChange={(open) => {
                            if (!year) return;
                            setMonthOpen(open);
                        }}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                disabled={!year}
                                className="w-full justify-between h-10 px-2.5 text-sm"
                            >
                                {month ? MONTH_LABELS[month] : "Select Month"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandInput placeholder="Search month..." />
                                <CommandEmpty>No month found.</CommandEmpty>
                                <CommandGroup>
                                    {months.map((m) => (
                                        <CommandItem
                                            key={m}
                                            value={MONTH_LABELS[m]}
                                            onSelect={() => {
                                                onMonthChange(m);
                                                setMonthOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    month === m ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            {MONTH_LABELS[m]}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Day</label>
                    <Popover open={dayOpen} onOpenChange={setDayOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                disabled={!month || !year}
                                className="w-full h-10 justify-between"
                            >
                                {day ? formatDayOrdinal(day) : 'Latest'}
                                <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[160px] p-0">
                            <Command>
                                <CommandGroup className="max-h-[200px] overflow-y-auto">
                                    {safeDays.map((d) => (
                                        <CommandItem
                                            key={d}
                                            onSelect={() => {
                                                onDayChange(d);
                                                setDayOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    day === d ? 'opacity-100' : 'opacity-0'
                                                )}
                                            />
                                            {formatDayOrdinal(d)}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Division</label>
                    <Popover open={divisionOpen} onOpenChange={setDivisionOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                disabled={divisions.length === 1}
                                className="w-full justify-between h-10 px-2.5 text-sm"
                            >
                                {selectedDivisionObj ? selectedDivisionObj.ou_name : "-- select --"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[250px] p-0">
                            <Command>
                                <CommandInput placeholder="Search division..." />
                                <CommandEmpty>No division found.</CommandEmpty>
                                <CommandGroup>
                                    {divisions.map((div) => (
                                        <CommandItem
                                            key={div.ou_code}
                                            value={div.ou_name}
                                            onSelect={() => {
                                                const newValue = selectedDivision === div.ou_code ? '' : div.ou_code;
                                                onDivisionChange(newValue);
                                                setDivisionOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selectedDivision === div.ou_code ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            {div.ou_name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Collection Resp.</label>
                    <Popover open={ecodeOpen} onOpenChange={setEcodeOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between h-10 px-2.5 text-sm truncate"
                            >
                                {selectedSubordinate ? selectedSubordinate.full_name : "-- select --"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[250px] p-0">
                            <Command>
                                <CommandInput placeholder="Search user..." />
                                <CommandEmpty>No user found.</CommandEmpty>
                                <CommandGroup className="max-h-[300px] overflow-y-auto">
                                    {subordinates.map((sub) => (
                                        <CommandItem
                                            key={sub.ecode}
                                            value={sub.full_name}
                                            onSelect={() => {
                                                const newValue = selectedEcode === sub.ecode ? '' : sub.ecode;
                                                onEcodeChange(newValue);
                                                setEcodeOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selectedEcode === sub.ecode ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            <span className="block max-w-[180px] truncate" title={sub.full_name}>
                                                {sub.full_name}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                        Payment Type
                    </label>

                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between h-10 px-2.5 text-sm"
                            >
                                {paymentType === 'to_pay'
                                    ? 'To Pay'
                                    : paymentType === 'to_be_billed'
                                        ? 'To be Billed'
                                        : paymentType === 'non_cn'
                                            ? 'Non CN'
                                            : '-- select --'}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandGroup>
                                    {PAYMENT_TYPE_OPTIONS.map((opt) => (
                                        <CommandItem
                                            key={opt.value}
                                            value={opt.label}
                                            onSelect={() => {
                                                const newVal: PaymentTypeValue =
                                                    paymentType === opt.value ? '' : opt.value;

                                                onPaymentTypeChange(newVal);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    paymentType === opt.value
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            {opt.label}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="min-w-0">
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                        Customer Status
                    </label>

                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between h-10 px-2.5 text-sm"
                            >
                                {customerStatus ? customerStatus.charAt(0).toUpperCase() + customerStatus.slice(1) : '-- select --'}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandGroup>
                                    {['active', 'inactive', 'legal'].map((status) => (
                                        <CommandItem
                                            key={status}
                                            value={status}
                                            onSelect={() => {
                                                const newVal = customerStatus === status ? '' : status;
                                                onCustomerStatusChange(newVal as CustomerStatusTypeValue);
                                            }}
                                        >
                                            <Check className={cn('mr-2 h-4 w-4', customerStatus === status ? 'opacity-100' : 'opacity-0')} />
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>


            <div className="flex items-end gap-4 w-full">
                <div className="min-w-0 w-[280px]">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Search Customer</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search by customer name..."
                            className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>
                </div>
                {paymentType === 'to_pay' && (
                    <div className="min-w-0 w-[250px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">Consignor</label>
                        <Popover open={consignorOpen} onOpenChange={setConsignorOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full justify-between h-10 px-2.5 text-sm truncate"
                                >
                                    {selectedConsignor
                                        ? consignors.find(c => c.cust_code === selectedConsignor)?.cust_name
                                        : "-- select --"}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                                </Button>
                            </PopoverTrigger>

                            <PopoverContent className="w-[250px] p-0">
                                <Command>
                                    <CommandInput placeholder="Search consignor..." />
                                    <CommandEmpty>No consignor found.</CommandEmpty>
                                    <CommandGroup className="max-h-[300px] overflow-y-auto">
                                        {consignors.map((c) => (
                                            <CommandItem
                                                key={c.cust_code}
                                                value={c.cust_name}
                                                onSelect={() => {
                                                    const newVal = selectedConsignor === c.cust_code ? '' : c.cust_code;
                                                    onConsignorChange(newVal);
                                                    setConsignorOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedConsignor === c.cust_code ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <span className="block max-w-[180px] truncate" title={c.cust_name}>
                                                    {c.cust_name}
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                )}

                <div className="flex items-end gap-2 ml-auto shrink-0 pb-[2px]">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === t ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                        >
                            {t}
                        </button>
                    ))}

                    {showDownloadRDDButton && (
                        <button
                            className="px-3 py-1 text-sm rounded-md border border-orange-600 text-orange-700 hover:bg-orange-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={onDownloadRDDExcel}
                            disabled={downloadingRDDExcel}
                        >
                            <Download size={16} />
                            {downloadingRDDExcel ? 'Downloading...' : 'RDD'}
                        </button>
                    )}

                    {showDownloadExcelButton && (
                        <button
                            className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={onDownloadExcel}
                            disabled={downloadingExcel}
                        >
                            <Download size={16} />
                            {downloadingExcel ? 'Downloading...' : 'Excel'}
                        </button>
                    )}

                    <button
                        className="px-3 py-1 text-sm rounded-md border border-purple-600 text-purple-700 hover:bg-purple-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onDownloadBulkExcel}
                        disabled={downloadingBulkExcel}
                    >
                        <Download size={16} />
                        {downloadingBulkExcel ? 'Downloading...' : 'Bulk Excel'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AraDropdowns;
